/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Box,
  CardActions,
  useTheme,
  alpha
} from '@mui/material';
import { useParams, useHistory, useLocation } from 'react-router';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import Loader from 'src/common/Loader';
import Newtransaction from 'src/components/transactions/NewTransaction';
import {
  getTransactionDetails,
  setTransactionType
} from 'src/redux/transactionSlice';
import { getUnreadNotificationsCount } from 'src/redux/notificationSlice';
import ROUTE from 'src/utils/routeConstants';
import { TRANSACTION_TYPES, USER_ROLES } from 'src/utils/constants';
import { FLEET_ONLY_TRANSACTION_TYPE, isFleetOnlyModeEnabled } from 'src/utils/appMode';
import {
  hasAdminLikeAccess,
  getFormattedDate,
  getFuelQuantity
} from 'src/utils/getInitials';
import { hasConfiguredFuelVolumeUnit } from 'src/utils/volumeUtils';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import useAuth from 'src/context/AuthContext';
import { getDispenserDisplayValue } from './transactionService';
import UpdateOdometerModal from './formModel/updateOdometerModal';

const TIME_FIELDS = [
  'start_time',
  'end_time',
  'order_date',
  'delivery_date'
];

const HIDDEN_FIELDS = [
  'mapped_with_request',
  'mapped_with_reconciliation',
  'dispenser_tank_name',
  'site'
];

const VOLUME_FIELDS = new Set([
  'quantity',
  'actual_filled',
  'delivered_dip_quantity',
  'delivered_measured_quantity_FLS',
  'delivered_measured_quantity_meter',
  'spillage_quantity',
  'shortage_quantity',
  'opening_stock',
  'closing_stock'
]);

// ---- CLEAN INFO BLOCK (Same style as SiteDetails) ----

function InfoBlock({ label, value }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        maxHeight: 'calc(100vh - 140px)',
        height: '100%',
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 0.5
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="body1"
        fontWeight={600}
        sx={{
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word'
        }}
      >
        {value ?? '--'}
      </Typography>
    </Box>
  );
}

function TransactionDetails() {
  const theme = useTheme();
  const { transactionId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { loading, transactionType, transactionDetails } = useSelector((state) => state.transactions);
  const { appSettings } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);

  const { getUser } = useAuth();
  const user = getUser();
  const hasAdminAccess = hasAdminLikeAccess(getUser);
  const isApprover = user?.role === USER_ROLES.approver;

  const [openModal, setOpenModal] = useState(false);
  const [isOdometerModalOpen, setIsOdometerModalOpen] = useState(false);

  const queryTransactionType = new URLSearchParams(location.search).get('type');
  const effectiveTransactionType = isFleetOnlyMode
    ? FLEET_ONLY_TRANSACTION_TYPE
    : (queryTransactionType || transactionType);

  useEffect(() => {
    if (isFleetOnlyMode) {
      if (transactionType !== FLEET_ONLY_TRANSACTION_TYPE) {
        dispatch(setTransactionType(FLEET_ONLY_TRANSACTION_TYPE));
      }
      return;
    }

    if (queryTransactionType && queryTransactionType !== transactionType) {
      dispatch(setTransactionType(queryTransactionType));
    }
  }, [dispatch, isFleetOnlyMode, queryTransactionType, transactionType]);

  useEffect(() => {
    dispatch(
      getTransactionDetails({ transactionType: effectiveTransactionType, transactionId })
    ).unwrap();
  }, [dispatch, transactionId, transactionType, queryTransactionType, effectiveTransactionType]);

  if (loading) return <Loader />;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Newtransaction
        open={openModal}
        initialValues={transactionDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`
        }}
      >
        {/* HEADER (Same style as SiteDetails) */}
        <CardHeader
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            bgcolor: 'background.paper'
          }}
          title={(
            <Box>
              <Typography variant="h6" fontWeight={700}>
                TRANSACTION DETAILS
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and manage transaction configuration
              </Typography>
            </Box>
          )}
          action={(
            <Button
              variant="outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => history.replace(ROUTE.transactions)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main
                }
              }}
            >
              Back
            </Button>
          )}
        />

        {/* CONTENT */}
        <CardContent sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
          <Grid container spacing={3}>
            {transactionDetails
              && Object.keys(transactionDetails)
                .filter((key) => !HIDDEN_FIELDS.includes(key) && shouldDisplayDetailField(key))
                .map((key) => {
                  if (key.endsWith('_gallons')) return null;
                  const isTimeField = TIME_FIELDS.includes(key);
                  const isVolumeField = VOLUME_FIELDS.has(key);
                  let displayValue = transactionDetails[key];
                  if (isTimeField) {
                    displayValue = transactionDetails[key]
                      ? getFormattedDate(transactionDetails[key])
                      : '--';
                  }
                  if (key === 'address') {
                    displayValue = getDispenserDisplayValue(transactionDetails);
                  }
                  if (isVolumeField && (hasConfiguredFuelVolumeUnit(user)
                    || transactionDetails[`${key}_gallons`] != null)) {
                    displayValue = getFuelQuantity(
                      transactionDetails[key],
                      transactionDetails[`${key}_gallons`]
                    );
                  }

                  return (
                    <Grid item xs={12} sm={6} md={4} key={key} sx={{ minWidth: 0 }}>
                      <InfoBlock
                        label={key
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                        value={displayValue}
                      />
                    </Grid>
                  );
                })}
          </Grid>
        </CardContent>

        {/* UPDATE ODOMETER (OUTWARD) */}
        {effectiveTransactionType === TRANSACTION_TYPES[0].id
          && (hasAdminAccess || isApprover) && (
            <>
              <Divider />
              <CardActions sx={{
                p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
              }}
              >
                <Button
                  startIcon={<EditIcon />}
                  variant="contained"
                  onClick={() => setIsOdometerModalOpen(true)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Edit Transaction
                </Button>
              </CardActions>
            </>
        )}

        {/* UPDATE INWARD TRANSACTION */}
        {effectiveTransactionType === TRANSACTION_TYPES[1].id
          && (hasAdminAccess || isApprover) && (
            <>
              <Divider />
              <CardActions sx={{
                p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
              }}
              >
                <Button
                  startIcon={<EditIcon />}
                  variant="contained"
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Update Transaction
                </Button>
              </CardActions>
            </>
        )}
        {effectiveTransactionType === TRANSACTION_TYPES[3].id
          && hasAdminAccess && (
            <>
              <Divider />
              <CardActions sx={{
                p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
              }}
              >
                <Button
                  startIcon={<EditIcon />}
                  variant="contained"
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Update Transaction
                </Button>
              </CardActions>
            </>
        )}
      </Card>

      {/* ODOMETER MODAL */}
      <UpdateOdometerModal
        open={isOdometerModalOpen}
        transactionId={Number(transactionId)}
        currentValue={transactionDetails?.odometer_entry}
        vehicleNumber={transactionDetails?.vehicle_number}
        siteId={transactionDetails?.site_id ?? transactionDetails?.site ?? null}
        additionalEntry={transactionDetails?.additional_entry}
        assetCodeMappingEnabled={Boolean(appSettings?.asset_code_mapping_enabled)}
        onSuccess={() => {
          dispatch(getTransactionDetails({ transactionType: effectiveTransactionType, transactionId }));
          dispatch(getUnreadNotificationsCount());
        }}
        onClose={() => setIsOdometerModalOpen(false)}
      />
    </Container>
  );
}

export default TransactionDetails;
